import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { BugReports, type BugReportSeverity } from '@/lib/api/v1';
import { Button, Icon, Modal, Textarea } from '@/design-system';
import { useToasts } from '@/lib/store';
import { appBuildSha } from '@/lib/version';

const AREAS = [
  { value: 'scanner', label: 'Scanner' },
  { value: 'barcode', label: 'Strekkode' },
  { value: 'pairing', label: 'Mat-paring' },
  { value: 'theme', label: 'Lys/mørk' },
  { value: 'app', label: 'Annet' },
] as const;

const SEVERITIES: { value: BugReportSeverity; label: string }[] = [
  { value: 'normal', label: 'Vanlig' },
  { value: 'high', label: 'Stopper meg' },
  { value: 'low', label: 'Småting' },
];

export function BugReporter() {
  const location = useLocation();
  const { push } = useToasts();
  const [open, setOpen] = useState(false);
  const [area, setArea] = useState<(typeof AREAS)[number]['value']>('app');
  const [severity, setSeverity] = useState<BugReportSeverity>('normal');
  const [message, setMessage] = useState('');

  const currentUrl = useMemo(() => {
    if (typeof window === 'undefined') return location.pathname;
    return `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
  }, [location.hash, location.pathname, location.search]);

  const submit = useMutation({
    mutationFn: () =>
      BugReports.create({
        area,
        severity,
        message: message.trim(),
        url: currentUrl,
        userAgent: navigator.userAgent,
        client: collectClientContext(),
      }),
    onSuccess: (report) => {
      push({ title: 'Bug rapportert', description: `ID ${report.id.slice(0, 8)}`, tone: 'success' });
      setMessage('');
      setSeverity('normal');
      setArea('app');
      setOpen(false);
    },
    onError: (err: Error) => {
      push({ title: 'Kunne ikke sende bugrapport', description: err.message, tone: 'danger' });
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-3 z-40 inline-flex h-11 w-11 items-center justify-center rounded-full border border-line bg-ink-soft/95 text-candle shadow-elevated backdrop-blur transition-colors hover:border-candle lg:bottom-5 lg:right-5"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5.75rem)' }}
        aria-label="Rapporter bug"
        title="Rapporter bug"
      >
        <Icon name="warning" size={18} />
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Rapporter bug" size="md">
        <div className="grid gap-4">
          <div className="grid gap-2">
            <span className="text-xs uppercase tracking-widest text-mist">Område</span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {AREAS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setArea(opt.value)}
                  className={`min-h-11 rounded-md border px-3 py-2 text-sm ${
                    area === opt.value ? 'border-candle bg-candle/10 text-candle' : 'border-line text-mist'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-2">
            <span className="text-xs uppercase tracking-widest text-mist">Alvorlighet</span>
            <div className="grid grid-cols-3 gap-2">
              {SEVERITIES.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSeverity(opt.value)}
                  className={`min-h-11 rounded-md border px-3 py-2 text-sm ${
                    severity === opt.value ? 'border-candle bg-candle/10 text-candle' : 'border-line text-mist'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <Textarea
            label="Hva skjedde?"
            rows={5}
            value={message}
            placeholder="F.eks. skannet strekkode 49131, fikk ingen match, eller knappen reagerte ikke."
            onChange={(e) => setMessage(e.target.value)}
          />

          <div className="rounded-md border border-line bg-overlay-soft p-3 text-xs text-mist">
            Sender også side, nettleser, skjermstørrelse og build. Ingen skjermbilde eller passordfelt tas med.
          </div>

          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="ghost" onClick={() => setOpen(false)}>Avbryt</Button>
            <Button
              variant="primary"
              iconLeft="warning"
              loading={submit.isPending}
              disabled={message.trim().length < 2}
              onClick={() => submit.mutate()}
            >
              Send
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

function collectClientContext(): Record<string, unknown> {
  const nav = navigator as Navigator & {
    standalone?: boolean;
    connection?: { effectiveType?: string; downlink?: number; rtt?: number };
  };
  const orientation =
    typeof screen !== 'undefined' && 'orientation' in screen
      ? { type: screen.orientation?.type, angle: screen.orientation?.angle }
      : undefined;

  return {
    buildSha: appBuildSha,
    language: navigator.language,
    languages: navigator.languages,
    online: navigator.onLine,
    standalone: Boolean(nav.standalone || window.matchMedia?.('(display-mode: standalone)').matches),
    touchPoints: navigator.maxTouchPoints,
    viewport: { width: window.innerWidth, height: window.innerHeight, dpr: window.devicePixelRatio },
    screen: { width: screen.width, height: screen.height, availWidth: screen.availWidth, availHeight: screen.availHeight },
    orientation,
    connection: nav.connection
      ? {
          effectiveType: nav.connection.effectiveType,
          downlink: nav.connection.downlink,
          rtt: nav.connection.rtt,
        }
      : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    localTime: new Date().toISOString(),
  };
}
